import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { pick } from 'lodash';

/** custom hooks */
import useQuery from 'refactored/hooks/useQuery';
import { SegmentResponse, useSegments } from 'refactored/hooks/useSegment';

/** custom components */
import PageHeader from 'components/Common/PageHeader';
import { selectUserAttributesState } from 'state/selectors/auth';
import Toast, { Type as ToastType } from 'components/Common/Toast';

/* utils */
import makeColumns from 'refactored/utils/segments/makeColumns';
import Roles from 'enums/users/roles.enum';

import TableConfig from 'enums/table/table.config';

import LayoutGap from '@/components/Common/LayoutGap';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { PaginationCursor } from '@/components/ui/pagination-cursor';
import type { CursorQuery } from '@/types';

export type Segment = {
  segmentId: string;
  name: string;
  status: string;
  size: number;
  created: string;
};

const Segments = () => {
  const [query, updateQuery] = useQuery<CursorQuery>({
    pagination: { limit: 1000 },
  });

  // Keep track of previous keys for back navigation
  const [previousKeys, setPreviousKeys] = useState<string[]>([]);

  const [
    {
      data: { results: segments, nextKey, previousKey } = {},
      error: errorFetchSegments,
      loading: loadingFetchSegments,
    },
  ] = useSegments({
    ...query.pagination,
    includeRules: true,
  });

  useEffect(() => {
    if (previousKey && previousKeys.length === 0) {
      setPreviousKeys([previousKey]);
    }
  }, [previousKey, previousKeys, setPreviousKeys]);

  const { roles: userRoles } = useSelector(
    selectUserAttributesState,
    shallowEqual,
  );

  /* table columns */
  const columns = useMemo(() => {
    const allColumns = makeColumns();

    const filteredColumns = allColumns.filter((column) => {
      const allowedRoles = column.meta?.allowedRoles;

      if (!allowedRoles) return true;

      // Convert user roles to Roles enum values
      const userRolesEnum = userRoles
        .map((role: string) => {
          const normalizedRole = role.toLowerCase();

          switch (normalizedRole) {
            case Roles.KardAdmin.toLowerCase():
              return Roles.KardAdmin;
            case Roles.DataAdmin.toLowerCase():
              return Roles.DataAdmin;
            case Roles.KardReporter.toLowerCase():
              return Roles.KardReporter;
            case Roles.Issuer.toLowerCase():
              return Roles.Issuer;
            default:
              return null;
          }
        })
        .filter(Boolean) as Roles[];

      const hasAccess = userRolesEnum.some((userRole) =>
        allowedRoles.includes(userRole),
      );

      return hasAccess;
    });

    return filteredColumns;
  }, [userRoles]);

  const [searchTerm, setSearchTerm] = useState('');

  const data = useMemo(() => {
    const isMatchingSearchTerm = (segment: SegmentResponse) =>
      segment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      segment.id.toLowerCase().includes(searchTerm.toLowerCase());

    const chooseSegmentFields = (segment: SegmentResponse) => {
      const { id: segmentId, ...rest } = pick(segment, [
        'id',
        'name',
        'status',
        'size',
        'created',
      ]);

      return { segmentId, ...rest };
    };

    const filteredSegments = segments
      ?.filter(isMatchingSearchTerm)
      .map(chooseSegmentFields);

    return filteredSegments;
  }, [segments, searchTerm]);

  const handleNextPage = () => {
    if (!nextKey) return;

    // Save current key before moving to next page
    const currentKey = query.pagination?.startKey;
    if (currentKey) {
      setPreviousKeys((prev) => [...prev, currentKey]);
    }

    updateQuery({
      pagination: {
        limit: TableConfig.LIMIT,
        startKey: JSON.stringify(nextKey),
      },
    });
  };

  const handlePreviousPage = () => {
    // Get the last key from the stack
    const newPreviousKeys = [...previousKeys];
    const startKey = newPreviousKeys.pop();
    setPreviousKeys(newPreviousKeys);

    updateQuery({
      pagination: {
        limit: TableConfig.LIMIT,
        startKey,
      },
    });
  };

  return (
    <>
      {errorFetchSegments && (
        <Toast
          id="fetch segments"
          text="Error fetching the segments"
          type={ToastType.Error}
        />
      )}

      <LayoutGap direction="column">
        <PageHeader title="Segments" className="mx-6 mt-4">
          <Link to="/segments/create">
            <Button>Create Segment</Button>
          </Link>
        </PageHeader>
        <LayoutGap direction="column" className="mx-6">
          <Input
            placeholder="Filter segments by name or ID"
            className="w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </LayoutGap>

        <div className="relative h-[calc(100vh-11rem)] overflow-y-auto">
          <div className="min-w-full">
            <DataTable
              columns={columns}
              data={data || []}
              loading={loadingFetchSegments}
              emptyMessage="No segments found"
              error={errorFetchSegments}
            />
          </div>
          <div className="sticky bottom-0 flex items-center justify-center border-t bg-white px-6 py-2">
            <PaginationCursor
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              hasNextPage={!!nextKey}
              hasPreviousPage={!!previousKey}
              isLoading={loadingFetchSegments}
            />
          </div>
        </div>
      </LayoutGap>
    </>
  );
};

export default Segments;
